import { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Card, Loader, Stack, Text } from '@wapharma/design-system';
import { apiPost } from '../api';
import { unwrapData } from '@wapharma/shared';;
import { getDisplayError } from '../apiErrors';
import { AdminAuthLayout } from '../components/auth/AdminAuthLayout';
import { PasswordInput } from '../components/PasswordInput';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getToken } from '../auth';
import authStyles from '../components/auth/auth-layout.module.css';
import { authFooterLine, resolveDeployOrgBrand } from '../lib/deployOrgBrand';

type PasswordChangeRes = {
  accessToken: string;
  email: string;
  roles: string[];
  mustChangePassword: boolean;
};

export function ForcePasswordChangePage() {
  const { user, loading, mustChangePassword, applyPasswordChange } = useAuth();
  const { showToast } = useToast();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const deployBrand = resolveDeployOrgBrand();

  const submit = useCallback(async () => {
    setFieldError(undefined);
    const normalizedOldPassword = oldPassword.trim();
    const normalizedNewPassword = newPassword.trim();
    const normalizedConfirmPassword = confirmPassword.trim();
    if (!normalizedOldPassword) {
      setFieldError('Le mot de passe actuel est requis.');
      return;
    }
    if (normalizedNewPassword.length < 8) {
      setFieldError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (normalizedNewPassword !== normalizedConfirmPassword) {
      setFieldError('Les mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    try {
      const token = getToken();
      const raw = await apiPost<{ data: PasswordChangeRes }>(
        '/auth/password-change',
        { oldPassword: normalizedOldPassword, newPassword: normalizedNewPassword },
        token ?? undefined,
      );
      const d = unwrapData(raw) as PasswordChangeRes;
      await applyPasswordChange(d.accessToken);
      showToast('Mot de passe mis à jour.', { tone: 'success' });
    } catch (e) {
      showToast(getDisplayError(e), { tone: 'error' });
    } finally {
      setSubmitting(false);
    }
  }, [applyPasswordChange, confirmPassword, newPassword, oldPassword, showToast]);

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', background: 'var(--wp-surface-page)' }}>
        <Loader />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!mustChangePassword) return <Navigate to="/" replace />;

  return (
    <AdminAuthLayout
      brandTitle="Sécurité du compte"
      brandBody="Remplacez le mot de passe temporaire par un mot de passe personnel et robuste avant d'accéder à l'administration."
    >
      <Stack gap="xl" className={authStyles.formStack}>
        <Card elevation="flat" padding="lg" className={authStyles.authFormCard}>
          <Stack gap="lg">
            <Stack gap="sm">
              <div className={authStyles.formHeading}>
                <p className={authStyles.formTitle}>Nouveau mot de passe</p>
              </div>
              <hr className={authStyles.formHeadingDivider} />
            </Stack>

            {user.email ? (
              <div className={authStyles.identityCard}>
                <p className={authStyles.identityEmail}>{user.email}</p>
              </div>
            ) : null}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              noValidate
            >
              <Stack gap="md">
                <PasswordInput
                  label="Mot de passe actuel"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  error={fieldError && !oldPassword ? fieldError : undefined}
                />
                <PasswordInput
                  label="Nouveau mot de passe"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Au moins 8 caractères"
                />
                <PasswordInput
                  label="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Répétez le mot de passe"
                  error={fieldError && oldPassword ? fieldError : undefined}
                />
                <Button type="submit" disabled={submitting} className={authStyles.submitBtn}>
                  {submitting ? 'Enregistrement…' : 'Enregistrer et continuer'}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Card>

        <Text size="sm" c="secondary" ta="center" ff="body" className={authStyles.mobileFooter}>
          {authFooterLine(deployBrand)}
        </Text>
      </Stack>
    </AdminAuthLayout>
  );
}
